// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { CreditCardIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Elements, PaymentElement, useElements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { navigate } from 'vike/client/router';

import Checkbox from '@/components/Forms/Checkbox';
import FormControl from '@/components/Forms/FormControl';
import { useDarkMode } from '@/renderer/utils/hooks/useDarkMode';
import { mixpanelTrack } from '@/renderer/utils/stat/mixp';
import useMe from '@/renderer/utils/useMe';
import { useRewardful } from '@/renderer/utils/useRewardful';

import Modal from './Modal';
import type { StripeOrderResponse } from './service';
import { createLocalOrder, createStripeOrder, isDuplicateConfirmError } from './service';

let stripePromise: Promise<Stripe | null> | undefined;
let stripeFailed = false;

function doPlausible(name: string) {
  try {
    window.plausible?.(name);
  } catch (_) {
    //
  }
}

// Allow re-running of `loadStripe` in case it fails
const getStripePromise = () => {
  if (stripeFailed || !stripePromise) {
    stripePromise = loadStripe(import.meta.env.VITE_PUBLIC_ENV__STRIPE_PUBLIC_KEY);

    stripeFailed = false;

    stripePromise.catch((_error) => {
      stripeFailed = true;
    });

    return stripePromise;
  }

  return stripePromise;
};

function StripeForm({
  ouuid,
  due,
  header,
  isLoading,
  isOpen,
  productId,
  offer,
  setIsLoading,
  setIsOpen,
  setTermsChecked,
  // source,
  stripe,
  termsChecked,
  title,
}: {
  ouuid?: string;
  due: string;
  header: JSX.Element;
  isLoading: boolean;
  isOpen: boolean;
  productId: string;
  offer?: API.Offer;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setTermsChecked: React.Dispatch<React.SetStateAction<boolean>>;
  source: string;
  stripe?: Stripe | null;
  termsChecked?: boolean;
  title?: string;
}) {
  // const [data, _setData] = useState<StripeOrderResponse>();
  const [message, setMessage] = useState('');
  const elements = useElements();
  const rewardful = useRewardful();
  const affiliate = useMe()?.referral || rewardful || '';
  let [timeDuration, timeUnit] = offer?.time?.subscription?.interval
    ? offer.time.subscription.interval.split(' ')
    : [undefined, undefined];

  if (timeDuration === '1') {
    timeUnit = timeUnit?.replace(/s$/, ''); // Remove trailing 's' if it exists
  }
  const recurrenceString = timeDuration
    ? `every${timeDuration !== '1' ? ` ${timeDuration}` : ''} ${timeUnit}`
    : undefined; // ie. every X months

  const handleTermsCheckboxChange = () => {
    setTermsChecked(!termsChecked);
  };

  const handleSubmit = async (event?: SubmitEvent) => {
    event?.preventDefault?.();

    if (isLoading || !stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    doPlausible('order/submit');
    mixpanelTrack('order/client:submit', { order_type: 'stripe', product_id: productId, ouuid });
    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.protocol}//${window.location.host}/order/confirmation?productId=${productId}`,
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error?.type === 'card_error' || error?.type === 'validation_error') {
      setMessage(error.message || 'Unknown Error');

      doPlausible('order/error/stripe');
      mixpanelTrack('order/client:error', { order_type: 'stripe', product_id: productId, ouuid });
    } else if (error) {
      setMessage('An unexpected error occured.');

      doPlausible('order/error/stripe');
      mixpanelTrack('order/client:error', { order_type: 'stripe', product_id: productId, ouuid });
    } else {
      doPlausible('order/success/stripe');
      mixpanelTrack('order/client:success', { order_type: 'stripe', product_id: productId, ouuid });
    }

    setIsLoading(false);
  };

  return (
    <Modal
      buttons={{
        submit: {
          loading: isLoading,
          disabled: isLoading || !termsChecked,
          children: (
            <>
              <CreditCardIcon height={20} className="mr-1 size-5 shrink-0" /> Pay {due}
            </>
          ),
        },
      }}
      title={
        <>
          Secure Payments by
          <span
            style={{
              background:
                "transparent url(\"data:image/svg+xml;charset=UTF-8,%3c?xml version='1.0' encoding='utf-8'?%3e%3csvg version='1.1' id='Layer_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 468 222.5' style='enable-background:new 0 0 468 222.5;' xml:space='preserve'%3e%3cstyle type='text/css'%3e .st0%7bfill-rule:evenodd;clip-rule:evenodd;fill:%23635BFF;%7d %3c/style%3e%3cg%3e%3cpath class='st0' d='M414,113.4c0-25.6-12.4-45.8-36.1-45.8c-23.8,0-38.2,20.2-38.2,45.6c0,30.1,17,45.3,41.4,45.3 c11.9,0,20.9-2.7,27.7-6.5v-20c-6.8,3.4-14.6,5.5-24.5,5.5c-9.7,0-18.3-3.4-19.4-15.2h48.9C413.8,121,414,115.8,414,113.4z M364.6,103.9c0-11.3,6.9-16,13.2-16c6.1,0,12.6,4.7,12.6,16H364.6z'/%3e%3cpath class='st0' d='M301.1,67.6c-9.8,0-16.1,4.6-19.6,7.8l-1.3-6.2h-22v116.6l25-5.3l0.1-28.3c3.6,2.6,8.9,6.3,17.7,6.3 c17.9,0,34.2-14.4,34.2-46.1C335.1,83.4,318.6,67.6,301.1,67.6z M295.1,136.5c-5.9,0-9.4-2.1-11.8-4.7l-0.1-37.1 c2.6-2.9,6.2-4.9,11.9-4.9c9.1,0,15.4,10.2,15.4,23.3C310.5,126.5,304.3,136.5,295.1,136.5z'/%3e%3cpolygon class='st0' points='223.8,61.7 248.9,56.3 248.9,36 223.8,41.3 '/%3e%3crect x='223.8' y='69.3' class='st0' width='25.1' height='87.5'/%3e%3cpath class='st0' d='M196.9,76.7l-1.6-7.4h-21.6v87.5h25V97.5c5.9-7.7,15.9-6.3,19-5.2v-23C214.5,68.1,202.8,65.9,196.9,76.7z'/%3e%3cpath class='st0' d='M146.9,47.6l-24.4,5.2l-0.1,80.1c0,14.8,11.1,25.7,25.9,25.7c8.2,0,14.2-1.5,17.5-3.3V135 c-3.2,1.3-19,5.9-19-8.9V90.6h19V69.3h-19L146.9,47.6z'/%3e%3cpath class='st0' d='M79.3,94.7c0-3.9,3.2-5.4,8.5-5.4c7.6,0,17.2,2.3,24.8,6.4V72.2c-8.3-3.3-16.5-4.6-24.8-4.6 C67.5,67.6,54,78.2,54,95.9c0,27.6,38,23.2,38,35.1c0,4.6-4,6.1-9.6,6.1c-8.3,0-18.9-3.4-27.3-8v23.8c9.3,4,18.7,5.7,27.3,5.7 c20.8,0,35.1-10.3,35.1-28.2C117.4,100.6,79.3,105.9,79.3,94.7z'/%3e%3c/g%3e%3c/svg%3e \") no-repeat center center",
              display: 'inline-block',
              height: '2em',
              marginTop: -1,
              verticalAlign: 'middle',
              width: 50,
            }}
            title="Stripe"
          />
        </>
      }
      setIsOpen={setIsOpen}
      isOpen={isOpen}
      modalProps={{
        as: 'form',
        action: '/order',
        className: 'w-[340px]', // plausible-event-name=order/submit
        method: 'POST',
        onSubmit: handleSubmit,
      }}
    >
      <h4 className="mb-4 font-medium text-base">{title}</h4>

      {header}
      <PaymentElement />
      {!!affiliate && (
        <div className="mt-4">
          <FormControl
            name="affiliateTracked"
            label="Referral tracked"
            wrap={Checkbox}
            inline
            defaultChecked
            disabled
          />
        </div>
      )}

      <div className="mt-4">
        <FormControl
          id="agreeStripeTos"
          name="agreeStripeTos"
          label={
            <>
              I agree to the the {/* biome-ignore lint/a11y/noBlankTarget: local url */}
              <a href="/terms" target="_blank">
                Terms and Conditions
              </a>
              .
            </>
          }
          wrap={Checkbox}
          inline
          checked={termsChecked}
          onChange={handleTermsCheckboxChange}
          defaultChecked={termsChecked}
        />
      </div>

      {!!message && (
        <aside className="mt-2 flex flex-row items-center space-x-1 text-xs text-yellow-400">
          <ExclamationTriangleIcon className="h-4 w-auto text-yellow-400" aria-hidden="true" />
          <span>{message}</span>
        </aside>
      )}

      {!!offer?.time?.duration && (
        <div className="mt-4 text-xs text-zinc-700 dark:text-zinc-300">
          By submitting this form, you agree to be billed {due} {recurrenceString} until you cancel
          the subscription. Your first payment is due today.
        </div>
      )}
    </Modal>
  );
}

function LocalForm({
  due,
  header,
  isLoading,
  isOpen,
  productId,
  index,
  offer,
  setIsLoading,
  setIsOpen,
  setTermsChecked,
  termsChecked,
  title,
}: {
  due: string;
  header: JSX.Element;
  isLoading: boolean;
  isOpen: boolean;
  productId: number;
  index: number;
  offer?: API.Offer;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setTermsChecked: React.Dispatch<React.SetStateAction<boolean>>;
  termsChecked: boolean;
  title?: string;
}) {
  const [message, setMessage] = useState('');
  // Set when the server asks the buyer to confirm a repeat purchase of a
  // stackable play pack (#2056). Answering re-sends the same order with the
  // confirmation attached.
  const [confirmMessage, setConfirmMessage] = useState('');

  const handleTermsCheckboxChange = () => {
    setTermsChecked(!termsChecked);
  };

  const handleSubmit = async (event?: SubmitEvent, confirmDuplicate = false) => {
    event?.preventDefault?.();

    if (isLoading) {
      return;
    }

    doPlausible('order/submit');
    mixpanelTrack('order/client:submit', { order_type: 'local', product_id: productId });

    setMessage('');
    setConfirmMessage('');
    setIsLoading(true);

    try {
      const localOrder = await createLocalOrder(productId, index, confirmDuplicate);

      doPlausible('order/success/local');
      mixpanelTrack('order/client:success', {
        order_type: 'local',
        product_id: productId,
        ouuid: localOrder?.ouuid,
      });

      if (typeof location !== 'undefined') {
        // full page refresh on client side so that the new product shows up immediately
        location.href = `/order/confirmation?productId=${productId}.${index}&ouuid=${localOrder.ouuid}`;
      } else {
        navigate(`/order/confirmation?productId=${productId}.${index}&ouuid=${localOrder.ouuid}`);
      }
    } catch (error) {
      if (isDuplicateConfirmError(error)) {
        setConfirmMessage((error as Error).message);
      } else {
        setMessage((error as Error).message || 'Unknown Error');
      }
    }

    // if (error.type === "card_error" || error.type === "validation_error") {
    //   setMessage(error.message || 'Unknown Error');
    // } else if (error) {
    //   setMessage("An unexpected error occured.");
    // }

    setIsLoading(false);
  };

  return (
    <Modal
      buttons={{
        submit: {
          loading: isLoading,
          disabled: isLoading || !termsChecked,
          children: (
            <>
              <CreditCardIcon height={20} className="mr-1 size-5 shrink-0" /> Pay {due}
            </>
          ),
        },
      }}
      title="RAS Secure Payments"
      setIsOpen={setIsOpen}
      isOpen={isOpen}
      modalProps={{
        as: 'form',
        action: '/order',
        className: 'w-[340px]', // plausible-event-name=order/submit
        method: 'POST',
        onSubmit: handleSubmit,
      }}
    >
      <h4 className="mb-4 font-medium text-base">{title}</h4>
      No credit card required for this purchase.
      {header}
      <div className="mt-4">
        <FormControl
          id="agreeStripeTos"
          name="agreeStripeTos"
          label={
            <>
              I agree to the the {/* biome-ignore lint/a11y/noBlankTarget: */}
              <a href="/terms" target="_blank">
                Terms and Conditions
              </a>
              .
            </>
          }
          wrap={Checkbox}
          inline
          checked={termsChecked}
          onChange={handleTermsCheckboxChange}
          defaultChecked={termsChecked}
        />
      </div>
      {!!message && (
        <aside className="mt-2 flex flex-row items-center space-x-1 text-xs text-yellow-400">
          <ExclamationTriangleIcon className="h-4 w-auto text-yellow-400" aria-hidden="true" />
          <span>{message}</span>
        </aside>
      )}
      {!!confirmMessage && (
        <aside className="mt-2 text-xs text-yellow-400">
          <div className="flex flex-row items-center space-x-1">
            <ExclamationTriangleIcon
              className="h-4 w-auto shrink-0 text-yellow-400"
              aria-hidden="true"
            />
            <span>{confirmMessage}</span>
          </div>
          <button
            type="button"
            className="mt-1 cursor-pointer underline disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isLoading}
            onClick={() => handleSubmit(undefined, true)}
          >
            Yes, buy it again
          </button>
        </aside>
      )}
    </Modal>
  );
}

export default function StripeButton({
  product,
  index,
  offer,
  price,
  size,
  source,
  plays,
  plus,
}: {
  product?: API.MembershipProduct<true | false>;
  index: number;
  offer?: API.Offer;
  price: number;
  size: string;
  source: string;
  fullWidth?: boolean;
  plays?: number;
  plus?: boolean;
}) {
  const userId = useMe()?.id;
  const isLoggedIn = !!userId;

  const defaultMessage =
    isLoggedIn && price !== undefined
      ? offer?.time?.subscription
        ? 'Subscribe now'
        : 'Buy now'
      : 'Join now';

  const [data, setData] = useState<StripeOrderResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [stripe, setStripe] = useState<Stripe | null>();
  const [message, setMessage] = useState(defaultMessage);
  // Set when the order request is rejected before the payment form opens, e.g. the
  // server blocks a duplicate full-season purchase (#1338). Shown inline below the
  // button so the reason is visible right where the user clicked.
  const [errorMessage, setErrorMessage] = useState('');
  // Set when the server asks the buyer to confirm a repeat purchase of a
  // stackable play pack (#2056), rather than refusing it. Answering re-runs the
  // same order with the confirmation attached.
  const [confirmMessage, setConfirmMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const affiliate = useRewardful();

  useEffect(() => {
    if (plays) {
      setMessage(`Order ${plays}${plus ? '+' : ''} play${plays > 1 ? 's' : ''}`);
    }
  }, [plays, plus]);

  useEffect(() => {
    if (isLoggedIn) {
      getStripePromise()
        .then((obj: Stripe | null) => {
          setStripe(obj);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setMessage(`Click to reload ${defaultMessage} form. Error: ${error?.toString() || ''}`);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const balanceUsed = data?.calculated?.balanceUsed || 0;
  const due = data?.calculated
    ? data.calculated.price.toLocaleString(navigator.language, {
        style: 'currency',
        currency: 'USD',
      })
    : '(calculating)';
  const balanceUsedUSD =
    !!balanceUsed &&
    balanceUsed?.toLocaleString(navigator.language, {
      style: 'currency',
      currency: 'USD',
      // Drop the trailing .00 on whole-dollar credit so the note reads "$100", not "$100.00".
      maximumFractionDigits: balanceUsed % 1 === 0 ? 0 : 2,
    });
  // const balance = initialState?.currentUser?.balance || 0;
  // balance ? Math.max(price - balance, 0) : price
  // const balanceUsed = balance > price ? price : balance;

  const { isDarkMode } = useDarkMode();

  // Open checkout for this offer. Re-entry is guarded by `isLoading`, and the
  // button itself is disabled for the whole round trip, so an unresponsive-
  // looking page cannot be clicked into a second order (#2056).
  const startOrder = async (confirmDuplicate = false) => {
    let isLoadingStatus = isLoading;
    let stripeObj = stripe;

    if (!isLoggedIn) {
      mixpanelTrack('order/client:signin');

      navigate(`/signin?redirect=${source}`);

      return;
    }

    if (!stripeObj && !isLoadingStatus) {
      // stripe failed to load
      setIsLoading(true);
      isLoadingStatus = true;
      try {
        setMessage(defaultMessage);
        stripeObj = await getStripePromise();
        setStripe(stripeObj);
        setIsLoading(false);
        isLoadingStatus = false;
      } catch (error) {
        console.error(error);
        setMessage(`Click to reload ${defaultMessage} form. Error: ${error?.toString() || ''}`);
        setIsLoading(false);
      }
    }

    if (stripeObj && !isLoadingStatus) {
      setIsLoading(true);
      setErrorMessage('');
      setConfirmMessage('');
      mixpanelTrack('order/client:open');

      try {
        setData(await createStripeOrder(product?.id || 0, index, affiliate, confirmDuplicate));
        setIsOpen(true);
      } catch (error) {
        // Surface the rejection inline (the API handler also shows a global
        // alert). Duplicate blocks (#1338, #2056) land here before checkout; the
        // confirmable one offers the buyer a way through instead of a dead end.
        if (isDuplicateConfirmError(error)) {
          setConfirmMessage((error as Error)?.message || '');
        } else {
          setErrorMessage((error as Error)?.message || 'Unable to start this order.');
        }
      }

      setIsLoading(false);
    }
  };

  return (
    <>
      {
        <button
          className={`flex w-full cursor-pointer justify-center rounded-sm border-0 bg-emerald-500 px-8 py-3 font-medium text-base text-white shadow-xs [text-shadow:_0_1px_0_var(--tw-shadow-color,_rgb(0_0_0_/_40%))] hover:bg-emerald-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[loading=true]:animate-pulse data-[loading=true]:cursor-progress plausible-event-name=${isLoggedIn ? 'order/open' : 'order/signin'}`}
          id={`stripe_button_${product?.id}_${index}`}
          size={`${size}`}
          data-loading={isLoading && isLoggedIn}
          // Stay disabled for the whole in-flight request, not just until the
          // modal opens: the pulse alone still looked clickable (#2056).
          disabled={!product?.id || (isLoading && isLoggedIn && !!stripe)}
          aria-busy={isLoading && isLoggedIn}
          onClick={() => startOrder()}
        >
          {message}
        </button>
      }

      {!!errorMessage && (
        <aside className="mt-2 flex flex-row items-center space-x-1 text-xs text-yellow-400">
          <ExclamationTriangleIcon
            className="h-4 w-auto shrink-0 text-yellow-400"
            aria-hidden="true"
          />
          <span>{errorMessage}</span>
        </aside>
      )}

      {!!confirmMessage && (
        <aside className="mt-2 text-xs text-yellow-400">
          <div className="flex flex-row items-center space-x-1">
            <ExclamationTriangleIcon
              className="h-4 w-auto shrink-0 text-yellow-400"
              aria-hidden="true"
            />
            <span>{confirmMessage}</span>
          </div>
          <button
            type="button"
            className="mt-1 cursor-pointer underline disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isLoading}
            onClick={() => startOrder(true)}
          >
            Yes, buy it again
          </button>
        </aside>
      )}

      {!!data?.doLocalPurchase && (
        <LocalForm
          due={due}
          isLoading={isLoading}
          isOpen={isOpen}
          productId={product?.id || 0}
          index={index}
          offer={offer}
          header={!!balanceUsedUSD && `${balanceUsedUSD} credit applied`}
          title={product?.title}
          setIsLoading={setIsLoading}
          setIsOpen={setIsOpen}
          setTermsChecked={setTermsChecked}
          termsChecked={termsChecked}
        />
      )}

      {!!data?.clientSecret && (
        <Elements
          options={{
            clientSecret: data.clientSecret,
            loader: 'always',
            appearance: { theme: isDarkMode ? 'night' : 'stripe' },
          }}
          stripe={stripe}
        >
          <StripeForm
            ouuid={data?.ouuid}
            due={`${due}`}
            isLoading={isLoading}
            isOpen={isOpen}
            productId={`${product?.id}.${index}`}
            offer={offer}
            header={!!balanceUsedUSD && `${balanceUsedUSD} credit applied`}
            title={product?.title}
            setIsLoading={setIsLoading}
            setIsOpen={setIsOpen}
            setTermsChecked={setTermsChecked}
            source={source}
            stripe={stripe}
            termsChecked={termsChecked}
          />
        </Elements>
      )}
    </>
  );
}
